// Pure workspace-scope derivations extracted from Sidebar.tsx during the P5
// split. No React, no hooks — easy to unit-test in isolation.

interface ChannelLike {
  id: string;
  workspace_id?: string | null;
  type?: string | null;
  is_archived?: boolean | null;
  // Channels link to their owning space via `parent_id` (the self-FK); the
  // legacy `client_space_id` column never existed on `channels` (mig 120).
  parent_id?: string | null;
  members?: ReadonlyArray<{ user_id: string }> | null;
}

interface SpaceWithLinks {
  id: string;
  is_archived?: boolean | null;
  workspace_links?: ReadonlyArray<{ role: string }> | null;
}

interface MergedSpace<T extends SpaceWithLinks> {
  // Original fields preserved.
  data: T;
  _isLinked: boolean;
}

/**
 * Merge own + linked + collaborator client-space lists with `_isLinked`
 * computed once, dedup by id, drop archived. Returns the merged list with
 * the original space fields plus `_isLinked` as a sibling property — this
 * mirrors the inline implementation that the sidebar's render reads.
 */
export function mergeClientSpaces<T extends SpaceWithLinks>(
  ownSpacesRaw: ReadonlyArray<T> | null | undefined,
  linkedSpacesRaw: ReadonlyArray<T> | null | undefined,
  collaboratorSpacesRaw: ReadonlyArray<T> | null | undefined,
): Array<T & { _isLinked: boolean }> {
  const own = (ownSpacesRaw ?? []).map((s) => ({
    ...s,
    _isLinked:
      (s.workspace_links ?? []).some(
        (l: { role: string }) => l.role === "linked",
      ) || false,
  }));
  const linked = (linkedSpacesRaw ?? []).map((s) => ({
    ...s,
    _isLinked: true,
  }));
  const collab = (collaboratorSpacesRaw ?? []).map((s) => ({
    ...s,
    _isLinked:
      (s.workspace_links ?? []).some(
        (l: { role: string }) => l.role === "linked",
      ) || false,
  }));
  // Dedup by id (a space could appear in multiple queries)
  const seen = new Set<string>();
  const merged: Array<T & { _isLinked: boolean }> = [];
  for (const s of [...own, ...linked, ...collab]) {
    if (!seen.has(s.id)) {
      seen.add(s.id);
      merged.push(s);
    }
  }
  return merged.filter((s) => !s.is_archived);
}

/**
 * Set of channel IDs whose member list contains at least one collaborator.
 * Used for the handshake-icon badge in the sidebar's channel rows.
 */
export function computeChannelsWithCollaborators(
  channels: ReadonlyArray<ChannelLike>,
  collaboratorIds: ReadonlySet<string>,
): Set<string> {
  if (collaboratorIds.size === 0) return new Set<string>();
  const set = new Set<string>();
  for (const ch of channels) {
    if (
      ch.members?.some((m: { user_id: string }) =>
        collaboratorIds.has(m.user_id),
      )
    ) {
      set.add(ch.id);
    }
  }
  return set;
}

// Used purely to satisfy unused-import linting in consumers when only the
// type is needed for return-shape annotations elsewhere.
export type { MergedSpace };
